/**
 * useKeyboardShortcuts Hook
 * 
 * Global keyboard shortcuts:
 * - Ctrl+Z/Y: Undo/Redo
 * - Space: Capture camera keyframe
 * - G/R/S: Transform modes
 * - U/Esc: UV edit mode
 * 
 * @param {Object} handlers - Callback handlers
 * @returns {void}
 */

import { useEffect } from 'react'

export function useKeyboardShortcuts(handlers = {}) {
  const {
    onUndo,
    onRedo,
    onCaptureKeyframe,
    onTransformMode,
    onUVEditToggle,
    isRecordingCameraAnimation = false,
    uvEditMode = false,
    transformEnabled = false,
  } = handlers

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return
      }

      // Undo/Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        if (onUndo) onUndo()
      } else if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.shiftKey && e.key === 'z'))
      ) {
        e.preventDefault()
        if (onRedo) onRedo()
      }
      // Capture camera keyframe (Space)
      else if (
        e.code === 'Space' &&
        isRecordingCameraAnimation &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey
      ) {
        e.preventDefault()
        if (onCaptureKeyframe) onCaptureKeyframe()
      }
      // UV Edit Mode shortcuts
      else if (uvEditMode) {
        if (e.key.toLowerCase() === 'g') {
          if (onTransformMode) onTransformMode('translate')
        } else if (e.key.toLowerCase() === 'r') {
          if (onTransformMode) onTransformMode('rotate')
        } else if (e.key.toLowerCase() === 's') {
          if (onTransformMode) onTransformMode('scale')
        } else if (e.key === 'Escape') {
          if (onUVEditToggle) onUVEditToggle(false)
        }
      }
      // Transform controls shortcuts
      else if (transformEnabled) {
        if (e.key.toLowerCase() === 'g') {
          if (onTransformMode) onTransformMode('translate')
        } else if (e.key.toLowerCase() === 'r') {
          if (onTransformMode) onTransformMode('rotate')
        } else if (e.key.toLowerCase() === 's') {
          if (onTransformMode) onTransformMode('scale')
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [
    onUndo,
    onRedo,
    onCaptureKeyframe,
    onTransformMode,
    onUVEditToggle,
    isRecordingCameraAnimation,
    uvEditMode,
    transformEnabled,
  ])
}

